/**
 * Choosing what a screen plays.
 *
 * This is the step that turns a claimed television and a full media library into
 * a working screen, and it is easy to miss: building a playlist does not put it
 * anywhere. Until a playlist is chosen here the TV shows its "waiting for
 * content" card, which is correct and looks exactly like a fault.
 *
 * Dayparting — different playlists at lunch and dinner — is deliberately not
 * here. One everyday playlist is what almost every screen wants, and offering a
 * schedule builder before anyone has seen a picture on a wall puts the fiddly
 * decision first. The API already accepts the rules, so the editor can be added
 * without changing anything behind it.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { ScreenSchedule } from '@pbtv/shared';
import { errorMessage } from '../../lib/api.js';
import type { PlaylistRecord } from '../../lib/playlists.js';

interface Props {
  screenName: string;
  /** Playlists this screen's store can reach: its own, plus the shared library. */
  playlists: readonly PlaylistRecord[];
  currentPlaylistId: string | null;
  busy: boolean;
  error: unknown;
  onSubmit: (schedule: ScreenSchedule) => void;
  onCancel: () => void;
}

export function AssignPlaylistForm({
  screenName,
  playlists,
  currentPlaylistId,
  busy,
  error,
  onSubmit,
  onCancel,
}: Props): ReactElement {
  const [selected, setSelected] = useState<string>(currentPlaylistId ?? '');

  const options = useMemo(
    () => [...playlists].sort((left, right) => left.name.localeCompare(right.name)),
    [playlists],
  );

  const chosen = options.find((playlist) => playlist.playlistId === selected) ?? null;
  const changed = (currentPlaylistId ?? '') !== selected;

  const submit = (): void => {
    onSubmit({
      defaultPlaylistId: selected === '' ? null : selected,
      fallbackAssetId: null,
      // Empty means the default playlist runs whenever the store is open.
      rules: [],
    });
  };

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      noValidate
    >
      <p className="form__lede">
        {screenName} plays whatever is chosen here, on a loop, for as long as the store is open.
      </p>

      <label className="field">
        <span className="field__label">Playlist</span>
        <select
          className="input"
          value={selected}
          onChange={(event) => setSelected(event.currentTarget.value)}
          autoFocus
        >
          <option value="">Nothing — show the waiting card</option>
          {options.map((playlist) => (
            <option key={playlist.playlistId} value={playlist.playlistId}>
              {playlist.name}
              {playlist.storeId === null ? ' (shared)' : ''}
              {` — ${playlist.items.length} ${playlist.items.length === 1 ? 'item' : 'items'}`}
            </option>
          ))}
        </select>
      </label>

      {options.length === 0 ? (
        <p className="field__hint">
          There are no playlists for this store yet. Create one on the Playlists page first, then
          come back here to put it on this screen.
        </p>
      ) : null}

      {chosen !== null && chosen.items.length === 0 ? (
        <p className="alert alert--warning" role="status">
          That playlist is empty, so the screen will keep showing its waiting card. Add some media
          to it on the Playlists page.
        </p>
      ) : null}

      {error !== null && error !== undefined ? (
        <p className="alert alert--danger" role="alert">
          {errorMessage(error)}
        </p>
      ) : null}

      <p className="field__hint">
        The television checks in every 45 seconds, so it picks this up within about a minute. It
        downloads and verifies each file before showing it, so the first picture may take a little
        longer than that.
      </p>

      <div className="form__actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="button button--primary" disabled={busy || !changed}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
